import React from 'react';
import { FCAValidationField } from '../FCAValidationField';
import { MultiNetworkSelectionField } from '../MultiNetworkSelectionField';
import { SignupFormData, FormErrors, ValidationState } from '../../types';

interface FCANumberStepProps {
  formData: SignupFormData;
  errors: FormErrors;
  fcaValidation: ValidationState;
  updateField: (field: keyof SignupFormData, value: string | boolean | string[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  onStepComplete?: (stepName: string) => void;
}

export const FCANumberStep: React.FC<FCANumberStepProps> = ({
  formData,
  errors,
  fcaValidation,
  updateField,
  onNext,
  onPrevious,
  onStepComplete,
}) => {
  const isStepValid = () => {
    return (
      formData.fcaNumber &&
      formData.networkIds && formData.networkIds.length > 0 &&
      (fcaValidation.isValid === true || fcaValidation.isValid === null) && // Allow progression when service is unavailable
      !errors.fcaNumber &&
      !errors.networkIds
    );
  };

  const handleNext = () => {
    if (isStepValid()) {
      // Track step completion in HubSpot
      if (onStepComplete) {
        onStepComplete('fca_number');
      }
      onNext();
    }
  };

  // Convert networks to select options (not needed for MultiNetworkSelectionField)
  // const networkOptions = availableNetworks.map(network => ({
  //   value: network.id,
  //   label: network.displayName,
  // }));

  const handleNetworkChange = (networkIds: string[], networkNames: string[]) => {
    updateField('networkIds', networkIds);
    updateField('networkNames', networkNames);
    // Clear the other network name if "Other" is not selected
    if (!networkIds.includes('other')) {
      updateField('otherNetworkName', '');
    }
  };

  const handleOtherNetworkNameChange = (name: string) => {
    updateField('otherNetworkName', name);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          Got your FCA number handy?
        </h1>
        <p className="text-gray-600">
          You'll find this on your{' '}
          <a 
            href="https://register.fca.org.uk/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-700"
          >
            Financial Conduct Authority register entry
          </a>
          . It's 6-8 digits long. Once entered, we'll fetch your firm details to save you time.
        </p>
      </div>

      <div className="space-y-4">
        <FCAValidationField
          id="fcaNumber"
          name="fcaNumber"
          label="FCA Number"
          value={formData.fcaNumber}
          onChange={(value) => updateField('fcaNumber', value)}
          validation={fcaValidation}
          error={errors.fcaNumber}
          required
        />

        <div>
          <label htmlFor="icoNumber" className="block text-sm font-medium text-gray-700 mb-2">
            ICO Registration Number <span className="text-gray-500">(Optional)</span>
          </label>
          <input
            type="text"
            id="icoNumber"
            name="icoNumber"
            value={formData.icoNumber}
            onChange={(e) => updateField('icoNumber', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder="e.g., Z1234567"
          />
          <p className="mt-1 text-sm text-gray-500">
            Your Information Commissioner's Office registration number. Adding this now will speed up your Experian Onboarding.
          </p>
        </div>

        <MultiNetworkSelectionField
          values={formData.networkIds || []}
          onChange={handleNetworkChange}
          otherNetworkName={formData.otherNetworkName || ''}
          onOtherNetworkNameChange={handleOtherNetworkNameChange}
          error={errors.networkIds}
          otherNetworkError={errors.otherNetworkName}
          required
        />
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onPrevious}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Previous
        </button>
        
        <button
          type="button"
          onClick={handleNext}
          disabled={!isStepValid()}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
};
